import type {LucideIcon} from 'lucide-react'
import type {ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {cn} from '@/lib/utils'
import {IconCircle} from '@/components/shared/IconCircle'
import {Icon} from '@/components/shared/Icon'

type EntityCardLinkDensity = 'comfortable' | 'compact'

interface EntityCardLinkProps {
    /** Destination route. When omitted, the card renders as a disabled/placeholder row (no link). */
    to?: string
    icon: LucideIcon
    children: ReactNode
    subtitle?: ReactNode
    /** `comfortable` (default): p-4 + circle icon. `compact`: p-3 + inline icon — matches older detail-page rows. */
    density?: EntityCardLinkDensity
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const comfortableBase = 'flex items-center gap-3 p-4 border rounded-lg'
const compactBase = 'flex items-center gap-2 p-3 border rounded-lg'
const linkHover = 'hover:bg-muted/50 transition-colors'
const disabledBg = 'bg-muted/20'
const linkIconColor = 'text-primary'
const mutedIconColor = 'text-muted-foreground'
const linkLabelClass = 'font-medium text-primary hover:underline'
const mutedLabelClass = 'text-muted-foreground'
/* eslint-enable i18next/no-literal-string */

export function EntityCardLink({to, icon, children, subtitle, density = 'comfortable', className, 'data-testid': testId}: EntityCardLinkProps) {
    const base = density === 'compact' ? compactBase : comfortableBase
    const iconNode = density === 'compact'
        ? <Icon icon={icon} size="lg" className={to ? linkIconColor : mutedIconColor}/>
        : <IconCircle icon={icon} size="md"/>
    const labelClass = to ? linkLabelClass : mutedLabelClass

    if (to) {
        return (
            <Link
                to={to}
                className={cn(base, linkHover, className)}
                data-testid={testId}
            >
                {iconNode}
                {density === 'compact' ? (
                    <span className={labelClass}>{children}</span>
                ) : (
                    <div>
                        <p className={labelClass}>{children}</p>
                        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
                    </div>
                )}
            </Link>
        )
    }

    return (
        <div className={cn(base, disabledBg, className)} data-testid={testId}>
            {iconNode}
            {density === 'compact' ? (
                <span className={labelClass}>{children}</span>
            ) : (
                <div>
                    <p className={labelClass}>{children}</p>
                    {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
                </div>
            )}
        </div>
    )
}
